import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import {
  Upload,
  X,
  FileText,
  Image as ImageIcon,
  Download,
  Trash2,
  Loader2,
  Eye,
  Paperclip,
  ZoomIn,
  ZoomOut,
  RotateCw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../../components/ui/dialog';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const RouteAttachments = forwardRef(({ routeId, isEditing = false }, ref) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // Files waiting to be uploaded (for new routes)
  
  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState(null);

  // Helper to check file type
  const isImageFile = (file) => {
    const type = file?.content_type?.toLowerCase() || '';
    return type.startsWith('image/') || file?.is_image;
  };

  const isPdfFile = (file) => {
    const type = file?.content_type?.toLowerCase() || '';
    const filename = file?.filename?.toLowerCase() || '';
    return type === 'application/pdf' || filename.endsWith('.pdf');
  };

  const isPreviewable = (file) => isImageFile(file) || isPdfFile(file);

  // Fetch existing attachments
  const fetchAttachments = useCallback(async () => {
    if (!routeId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/routes/${routeId}/attachments`);
      if (response.data.success) {
        setAttachments(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
    } finally {
      setLoading(false);
    }
  }, [routeId]);

  useEffect(() => {
    if (routeId && isEditing) {
      fetchAttachments();
    }
  }, [routeId, isEditing, fetchAttachments]);

  // Şub 10, 2026 — Paste-from-clipboard desteği
  // Ctrl+V ile pano içeriği (screenshot, kopyalanmış dosya) doğrudan yapıştırılır.
  // Modal açıkken document-level paste event'i dinlenir; input/textarea içine
  // odaklanmışsa native paste'e izin verilir (text yapıştırma kırılmasın).
  useEffect(() => {
    const onPaste = (e) => {
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      // text input içinde yapıştırıyorsa normal davranışı bırak
      if (tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable) return;
      const items = e.clipboardData?.items;
      if (!items || items.length === 0) return;
      const files = [];
      for (const it of items) {
        if (it.kind === 'file') {
          const f = it.getAsFile();
          if (f) {
            // Pano'dan gelen resimlerde isim genelde "image.png"; daha açıklayıcı yap
            if (!f.name || f.name === 'image.png') {
              const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
              const ext = (f.type.split('/')[1] || 'bin').toLowerCase();
              files.push(new File([f], `pasted-${ts}.${ext}`, { type: f.type }));
            } else {
              files.push(f);
            }
          }
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        toast.success(`${files.length} dosya panodan eklendi`);
        handleFiles(files);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
    e.target.value = ''; // Reset input
  };

  // Process files
  const handleFiles = async (files) => {
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: Dosya 10MB'dan büyük olamaz`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    if (routeId) {
      // Upload immediately if editing existing route
      for (const file of validFiles) {
        await uploadFile(file);
      }
    } else {
      // Queue files for new routes (will be uploaded after route is created)
      const newPending = validFiles.map(file => ({
        id: `pending_${Date.now()}_${Math.random()}`,
        file,
        filename: file.name,
        content_type: file.type,
        file_size: file.size,
        is_image: file.type.startsWith('image/'),
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      }));
      setPendingFiles(prev => [...prev, ...newPending]);
    }
  };

  // Upload single file
  const uploadFile = async (file) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/routes/${routeId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success(`${file.name} yüklendi`);
        fetchAttachments();
      }
    } catch (error) {
      toast.error(`${file.name} yüklenemedi`);
    } finally {
      setUploading(false);
    }
  };

  // Upload pending files (called after route is created)
  const uploadPendingFiles = async (newRouteId) => {
    for (const pending of pendingFiles) {
      try {
        const formData = new FormData();
        formData.append('file', pending.file);
        await api.post(`/routes/${newRouteId}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (error) {
        console.error('Error uploading pending file:', error);
      }
    }
    setPendingFiles([]);
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    uploadPendingFiles,
    getPendingFiles: () => pendingFiles,
    hasPendingFiles: () => pendingFiles.length > 0
  }));

  // Delete attachment
  const handleDelete = async (attachment) => {
    if (attachment.id.startsWith('pending_')) {
      // Remove from pending
      setPendingFiles(prev => prev.filter(p => p.id !== attachment.id));
      if (attachment.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
      return;
    }

    try {
      await api.delete(`/routes/${routeId}/attachments/${attachment.id}`);
      toast.success('Dosya silindi');
      fetchAttachments();
    } catch (error) {
      toast.error('Silme hatası');
    }
  };

  // Preview file
  const handlePreview = async (file) => {
    setPreviewFile(file);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setZoom(1);
    setRotation(0);
    setPdfPageNumber(1);
    setPdfNumPages(null);
    setPreviewUrl(null);

    // Handle pending files with local preview
    if (file.preview) {
      setPreviewUrl(file.preview);
      setPreviewLoading(false);
      return;
    }

    // Handle pending PDF files
    if (file.id?.startsWith('pending_') && file.file) {
      const url = URL.createObjectURL(file.file);
      setPreviewUrl(url);
      setPreviewLoading(false);
      return;
    }

    try {
      const response = await api.get(`/routes/${routeId}/attachments/${file.id}`);
      if (response.data.success) {
        const data = response.data.data;
        const byteCharacters = atob(data.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.content_type });
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      }
    } catch (error) {
      console.error('Preview error:', error);
      setPreviewError('Önizleme yüklenemedi');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Close preview and cleanup
  const closePreview = () => {
    if (previewUrl && !previewFile?.preview) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewOpen(false);
    setPreviewFile(null);
    setPreviewUrl(null);
    setPreviewError(null);
  };

  // PDF load success handler
  const onPdfLoadSuccess = ({ numPages }) => {
    setPdfNumPages(numPages);
  };

  // Download attachment
  const handleDownload = async (attachment) => {
    if (attachment.id.startsWith('pending_')) {
      // Download pending file
      const url = URL.createObjectURL(attachment.file);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      link.click();
      URL.revokeObjectURL(url);
      return;
    }

    try {
      const response = await api.get(`/routes/${routeId}/attachments/${attachment.id}`);
      if (response.data.success) {
        const data = response.data.data;
        const byteCharacters = atob(data.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.content_type });
        
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = data.filename;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error('İndirme hatası');
    }
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Combine attachments and pending files for display
  const allFiles = [...attachments, ...pendingFiles];

  // Expose uploadPendingFiles for parent component
  RouteAttachments.uploadPendingFiles = uploadPendingFiles;
  RouteAttachments.pendingFiles = pendingFiles;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Paperclip className="h-4 w-4 text-slate-500" />
        <span className="text-sm font-medium">Dosya Ekleri</span>
        {allFiles.length > 0 && (
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
            {allFiles.length}
          </span>
        )}
      </div>

      {/* Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-all cursor-pointer ${
          isDragging 
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
            : 'border-slate-200 dark:border-slate-700 hover:border-blue-300'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById('route-file-input').click()}
        data-testid="route-attachments-dropzone"
      >
        <input
          id="route-file-input"
          type="file"
          className="hidden"
          onChange={handleFileSelect}
          multiple
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        />
        <Upload className={`h-6 w-6 mx-auto mb-1 ${isDragging ? 'text-blue-500' : 'text-slate-400'}`} />
        <p className="text-xs text-slate-500">
          Dosyaları sürükleyin, tıklayın <span className="text-slate-400">veya</span> <kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded">Ctrl</kbd>+<kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded">V</kbd> ile yapıştırın
        </p>
        <p className="text-xs text-slate-400 mt-1">
          Resim, PDF, Word, Excel (max 10MB)
        </p>
      </div>

      {/* Files List */}
      {allFiles.length > 0 && (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {allFiles.map((file) => (
            <div
              key={file.id}
              className={`flex items-center gap-2 p-2 rounded-lg border ${
                file.id.startsWith('pending_') 
                  ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20' 
                  : 'bg-slate-50 border-slate-200 dark:bg-slate-800'
              }`}
            >
              {/* Icon/Thumbnail */}
              <div className="w-10 h-10 flex-shrink-0 rounded overflow-hidden bg-white dark:bg-slate-700 flex items-center justify-center">
                {file.is_image && (file.thumbnail || file.preview) ? (
                  <img 
                    src={file.preview || `data:${file.content_type};base64,${file.thumbnail}`}
                    alt={file.filename}
                    className="w-full h-full object-cover"
                  />
                ) : file.is_image ? (
                  <ImageIcon className="h-5 w-5 text-blue-500" />
                ) : (
                  <FileText className="h-5 w-5 text-slate-500" />
                )}
              </div>

              {/* File info */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{file.filename}</p>
                <p className="text-xs text-slate-400">
                  {formatSize(file.file_size)}
                  {file.id.startsWith('pending_') && (
                    <span className="ml-2 text-amber-600">Bekliyor</span>
                  )}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1">
                {isPreviewable(file) && (
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    className="h-7 w-7"
                    onClick={(e) => { e.stopPropagation(); handlePreview(file); }}
                    title="Önizle"
                    data-testid={`preview-btn-${file.id}`}
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-7 w-7"
                  onClick={(e) => { e.stopPropagation(); handleDownload(file); }}
                  title="İndir"
                  data-testid={`download-btn-${file.id}`}
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-7 w-7 text-red-500 hover:text-red-600"
                  onClick={(e) => { e.stopPropagation(); handleDelete(file); }}
                  title="Sil"
                  data-testid={`delete-btn-${file.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Loading indicator */}
      {(loading || uploading) && (
        <div className="flex items-center justify-center py-2">
          <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
          <span className="ml-2 text-xs text-slate-500">
            {uploading ? 'Yükleniyor...' : 'Yükleniyor...'}
          </span>
        </div>
      )}

      {/* File Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={closePreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="file-preview-modal">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-semibold flex items-center gap-2">
                {isPdfFile(previewFile) ? (
                  <FileText className="h-5 w-5 text-red-500" />
                ) : (
                  <ImageIcon className="h-5 w-5 text-blue-500" />
                )}
                <span className="truncate max-w-md">{previewFile?.filename || 'Önizleme'}</span>
                {isPdfFile(previewFile) && pdfNumPages && (
                  <Badge variant="outline" className="ml-2">{pdfPageNumber} / {pdfNumPages}</Badge>
                )}
              </DialogTitle>
            </div>
          </DialogHeader>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 py-2 border-b flex-shrink-0">
            {isPdfFile(previewFile) && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPdfPageNumber(p => Math.max(1, p - 1))}
                  disabled={pdfPageNumber <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">{pdfPageNumber} / {pdfNumPages || '?'}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPdfPageNumber(p => Math.min(pdfNumPages || p, p + 1))}
                  disabled={pdfPageNumber >= (pdfNumPages || 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <div className="border-l pl-4 flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setZoom(s => Math.max(0.5, s - 0.25))}
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setZoom(s => Math.min(2, s + 0.25))}
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
            {isImageFile(previewFile) && (
              <>
                <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.max(0.25, z - 0.25))}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
                <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.min(3, z + 0.25))}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setRotation(r => (r + 90) % 360)}>
                  <RotateCw className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-900 rounded-lg flex items-start justify-center min-h-[400px] p-4">
            {previewLoading ? (
              <div className="flex flex-col items-center gap-3 py-20">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <p className="text-sm text-slate-500 dark:text-slate-400">Yükleniyor...</p>
              </div>
            ) : previewError ? (
              <div className="text-center text-red-500 py-20">
                <p>{previewError}</p>
                <Button 
                  className="mt-4" 
                  variant="outline"
                  onClick={() => previewFile && handleDownload(previewFile)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Dosyayı İndir
                </Button>
              </div>
            ) : isImageFile(previewFile) && previewUrl ? (
              <div className="overflow-auto w-full h-full flex items-center justify-center">
                <img
                  src={previewUrl}
                  alt={previewFile?.filename || 'Preview'}
                  className="max-w-full transition-transform duration-200"
                  style={{ 
                    transform: `scale(${zoom}) rotate(${rotation}deg)`,
                    transformOrigin: 'center center'
                  }}
                />
              </div>
            ) : isPdfFile(previewFile) && previewUrl ? (
              <Document
                file={previewUrl}
                onLoadSuccess={onPdfLoadSuccess}
                onLoadError={(error) => {
                  console.error('PDF load error:', error);
                  setPreviewError('PDF yüklenemedi');
                }}
                loading={
                  <div className="flex items-center gap-2 py-20">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span>PDF yükleniyor...</span>
                  </div>
                }
                error={
                  <div className="text-center py-10 text-red-500">
                    <FileText className="h-12 w-12 mx-auto mb-2 text-red-400" />
                    <p>PDF yüklenemedi</p>
                    <Button className="mt-4" onClick={() => previewFile && handleDownload(previewFile)}>
                      <Download className="h-4 w-4 mr-2" />
                      PDF'i İndir
                    </Button>
                  </div>
                }
              >
                <Page
                  pageNumber={pdfPageNumber}
                  scale={zoom}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
              </Document>
            ) : (
              <div className="text-center p-8 py-20">
                <FileText className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <p className="text-slate-500 dark:text-slate-400 mb-4">Bu dosya türü önizlenemez</p>
                <Button onClick={() => previewFile && handleDownload(previewFile)}>
                  <Download className="h-4 w-4 mr-2" />
                  Dosyayı İndir
                </Button>
              </div>
            )}
          </div>

          {/* Footer */}
          <DialogFooter className="flex-shrink-0 border-t dark:border-slate-700 pt-4">
            <div className="flex justify-between items-center w-full">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {formatSize(previewFile?.file_size || 0)}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={closePreview}>
                  Kapat
                </Button>
                <Button onClick={() => previewFile && handleDownload(previewFile)}>
                  <Download className="h-4 w-4 mr-2" />
                  İndir
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

RouteAttachments.displayName = 'RouteAttachments';

export default RouteAttachments;
